import { useEffect, useRef } from 'react';

export const AUTO_SEARCH_DELAY = 500;

/**
 * Runs a search once the user pauses typing, so the Search button / Enter key is no
 * longer required. `value` is what is in the input, `committedValue` is the search
 * currently applied; nothing fires while they match (ignoring surrounding spaces), so
 * mounting, URL syncs and explicit submits don't trigger a duplicate search.
 */
const useAutoSearch = (
  value: string,
  committedValue: string,
  onCommit: (value: string) => void,
  delay: number = AUTO_SEARCH_DELAY
) => {
  // Callers usually pass an inline handler - keep the latest one without restarting the timer.
  const onCommitRef = useRef(onCommit);
  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  useEffect(() => {
    if (value.trim() === committedValue.trim()) return;
    const timer = setTimeout(() => onCommitRef.current(value), delay);
    return () => clearTimeout(timer);
  }, [value, committedValue, delay]);
};

export default useAutoSearch;
